import { useQuery } from '@tanstack/react-query';

import {
  FetchCompaniesRequest,
  companiesService,
  payCalendarsService,
  individualsService,
  companyConfigurationService,
} from '@aslanpay/portal-common-lib';

import { CompanyPage } from 'app/components/UI/Table/Table.types';

const useFetchCompaniesPage = ({
  ids,
  friendlyId,
  status,
  type,
  query,
  sortBy,
  sort,
  page,
  size,
}: FetchCompaniesRequest) => {
  return useQuery<CompanyPage, Error>(
    ['fetchCompaniesPage', ids, friendlyId, status, type, page, size, query, sortBy, sort],
    async () => {
      const companies = await companiesService.fetchCompanies({
        ids,
        friendlyId,
        status,
        type,
        page,
        size,
        query,
        sortBy,
        sort,
      });

      if (companies.list.length === 0) {
        return { ...companies, list: [] };
      }

      const companiesIds = companies.list.map((company) => company.id);

      const [payCalendarsCount, employeesCount] = await Promise.all([
        payCalendarsService.fetchPayCalendars({ companyId: companiesIds, page: 0, size: 1 }),
        individualsService.fetchIndividuals({
          role: ['EMPLOYEE'],
          companyId: companiesIds,
          page: 0,
          size: 1,
        }),
      ]);

      const [payCalendars, employees] = await Promise.all([
        payCalendarsCount.total > 0
          ? payCalendarsService.fetchPayCalendars({
              companyId: companiesIds,
              page: 0,
              size: payCalendarsCount.total,
            })
          : payCalendarsCount,
        employeesCount.total > 0
          ? individualsService.fetchIndividuals({
              role: ['EMPLOYEE'],
              companyId: companiesIds,
              page: 0,
              size: employeesCount.total,
            })
          : employeesCount,
      ]);

      const modules = await companyConfigurationService.fetchCompaniesConfigurations({
        companyIds: companiesIds,
        page: 0,
        size: companiesIds.length,
      });

      const getPayCalendarsCount = (companyId: string) =>
        payCalendars.list.reduce((acc, payCalendar) => {
          if (payCalendar.companyId === companyId) {
            return acc + 1;
          }

          return acc;
        }, 0);

      const getEmployeesCount = (companyId: string) =>
        employees.list.reduce((acc, employee) => {
          if (employee.companyId === companyId) {
            return acc + 1;
          }

          return acc;
        }, 0);

      const getEnabledServices = (companyId: string): string[] => {
        const companyModule = modules.list.find((module) => module.companyId === companyId);
        if (!companyModule) return [];

        return Object.entries(companyModule.modulesConfig.modules)
          .filter(([, value]) => value === 'ENABLED')
          .map(([key]) => key)
          .sort(([a], [b]) => a.localeCompare(b));
      };

      const companiesList = companies.list.map((company) => {
        const services = getEnabledServices(company.id);

        return {
          ...company,
          payCalendars: getPayCalendarsCount(company.id),
          employees: getEmployeesCount(company.id),
          activeServices: services.length,
          services, // this is an array containing the enabled services
        };
      });

      return { ...companies, list: companiesList };
    }
  );
};

export default useFetchCompaniesPage;
